import { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  List as ListIcon,
  Settings,
  Map,
  Schema,
  Description,
  Science,
  Psychology,
  SmartToy,
  DeveloperBoard,
  HealthAndSafety,
  Visibility,
  People,
} from '@mui/icons-material';
import ConfigList from './ConfigList';
import ConnectionSchema from './ConnectionSchema';
import MasterScreen from './MasterScreen';
import FormSchemaEditor from './FormSchemaEditor';
import PromptsEditor from './PromptsEditor';
import LabMapperEditor from './LabMapperEditor';
import SynonymManagement from './SynonymManagement';
import SystemAiConfig from './SystemAiConfig';
import DeploymentJetson from './DeploymentJetson';
import SyncHealthDashboard from './SyncHealthDashboard';
import UiSettingsPanel from './UiSettingsPanel';
import UserAccessManagement from './UserAccessManagement';
import { dhis2ConfigAPI, type Dhis2ConfigOut } from '../../services/api';

const SUB_NAV_ITEMS: { id: number; label: string; shortLabel: string; icon: React.ReactNode }[] = [
  { id: 0, label: 'Config list', shortLabel: 'Configs', icon: <ListIcon /> },
  { id: 1, label: 'Connection & Schema API', shortLabel: 'Connection', icon: <Settings /> },
  { id: 2, label: 'Master Screen', shortLabel: 'Master', icon: <Map /> },
  { id: 3, label: 'Form Schema', shortLabel: 'Schema', icon: <Schema /> },
  { id: 4, label: 'Prompts', shortLabel: 'Prompts', icon: <Description /> },
  { id: 5, label: 'Lab Mapper', shortLabel: 'Lab', icon: <Science /> },
  { id: 6, label: 'Synonyms', shortLabel: 'Synonyms', icon: <Psychology /> },
  { id: 7, label: 'System / AI', shortLabel: 'AI', icon: <SmartToy /> },
  { id: 8, label: 'Deployment / Jetson', shortLabel: 'Jetson', icon: <DeveloperBoard /> },
  { id: 9, label: 'Sync Health', shortLabel: 'Sync', icon: <HealthAndSafety /> },
  { id: 10, label: 'UI', shortLabel: 'UI', icon: <Visibility /> },
  { id: 11, label: 'Directory & access control', shortLabel: 'Directory', icon: <People /> },
];

export default function Dhis2Admin() {
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down('sm'));
  const [subIndex, setSubIndex] = useState(0);
  const [configs, setConfigs] = useState<Dhis2ConfigOut[]>([]);
  const [selectedConfigId, setSelectedConfigId] = useState<string | null>(null);

  const loadConfigs = async () => {
    try {
      const res = await dhis2ConfigAPI.listConfigs();
      setConfigs(Array.isArray(res.data) ? res.data : []);
    } catch {
      setConfigs([]);
    }
  };

  useEffect(() => {
    loadConfigs();
  }, []);

  const handleOpenConnection = (configId: string) => {
    setSelectedConfigId(configId);
    setSubIndex(1);
  };

  const handleOpenSynonyms = () => {
    setSubIndex(6);
  };

  const renderContent = () => {
    switch (subIndex) {
      case 0:
        return <ConfigList onOpenConnection={handleOpenConnection} onMutated={loadConfigs} />;
      case 1:
        return (
          <ConnectionSchema
            configs={configs}
            selectedConfigId={selectedConfigId}
            onSelectConfigId={setSelectedConfigId}
            onConfigUpdated={loadConfigs}
          />
        );
      case 2:
        return (
          <MasterScreen
            configs={configs}
            selectedConfigId={selectedConfigId}
            onSelectConfigId={setSelectedConfigId}
            onOpenSynonyms={handleOpenSynonyms}
          />
        );
      case 3:
        return (
          <FormSchemaEditor
            configs={configs}
            selectedConfigId={selectedConfigId}
            onSelectConfigId={setSelectedConfigId}
          />
        );
      case 4:
        return (
          <PromptsEditor
            configs={configs}
            selectedConfigId={selectedConfigId}
            onSelectConfigId={setSelectedConfigId}
          />
        );
      case 5:
        return (
          <LabMapperEditor
            configs={configs}
            selectedConfigId={selectedConfigId}
            onSelectConfigId={setSelectedConfigId}
          />
        );
      case 6:
        return (
          <SynonymManagement
            configs={configs}
            selectedConfigId={selectedConfigId}
            onSelectConfigId={setSelectedConfigId}
          />
        );
      case 7:
        return <SystemAiConfig />;
      case 8:
        return <DeploymentJetson />;
      case 9:
        return <SyncHealthDashboard configs={configs} />;
      case 10:
        return <UiSettingsPanel />;
      case 11:
        return <UserAccessManagement />;
      default:
        return null;
    }
  };

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <Typography variant={isSmall ? 'h6' : 'h5'} gutterBottom fontWeight={600} sx={{ flexShrink: 0 }}>
        Integration configs (DHIS2 & non-DHIS2)
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, flexShrink: 0 }} display={isSmall ? 'none' : 'block'}>
        Config list, schema, and mappings for DHIS2 or admin-defined (internal/stream-only). Connection & Sync apply to DHIS2 configs; Form Schema, Prompts, Lab Mapper, and Synonyms apply to all.
      </Typography>

      <Paper
        sx={{
          flexGrow: 1,
          minHeight: 0,
          display: 'flex',
          overflow: 'hidden',
          borderRadius: 2,
        }}
      >
        {/* Left pane – sub-tabs */}
        <Box
          sx={{
            width: { xs: 56, sm: 220 },
            minWidth: { xs: 56, sm: 220 },
            borderRight: 1,
            borderColor: 'divider',
            bgcolor: 'grey.50',
            display: 'flex',
            flexDirection: 'column',
            overflowY: 'auto',
            overflowX: 'hidden',
          }}
        >
          <List dense disablePadding sx={{ py: 0.5 }}>
            {SUB_NAV_ITEMS.map((item) => (
              <ListItemButton
                key={item.id}
                selected={subIndex === item.id}
                onClick={() => setSubIndex(item.id)}
                sx={{
                  py: { xs: 1.25, sm: 1.5 },
                  '&.Mui-selected': {
                    bgcolor: 'primary.main',
                    color: 'primary.contrastText',
                    '&:hover': { bgcolor: 'primary.dark' },
                    '& .MuiListItemIcon-root': { color: 'inherit' },
                  },
                }}
              >
                <ListItemIcon sx={{ minWidth: { xs: 40, sm: 44 }, color: subIndex === item.id ? 'inherit' : 'action.active' }}>
                  {item.icon}
                </ListItemIcon>
                <ListItemText
                  primary={isSmall ? item.shortLabel : item.label}
                  primaryTypographyProps={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, fontWeight: 500 }}
                  sx={{ display: { xs: 'none', sm: 'block' } }}
                />
              </ListItemButton>
            ))}
          </List>
        </Box>

        {/* Right pane – content */}
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            overflow: 'auto',
            p: { xs: 1.5, sm: 2 },
          }}
        >
          {renderContent()}
        </Box>
      </Paper>
    </Box>
  );
}
